import { forwardRef } from 'react'
import { cn } from '../../lib/cn'

type ButtonVariant = 'default' | 'primary' | 'intent'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant
	size?: ButtonSize
	fullWidth?: boolean
	children: React.ReactNode
	className?: string
}

/**
 * Button primitive — financial-grade dark.
 *
 *   `primary`  — solid `bg-primary` (slate-50 on slate-950). Reads as the
 *                terminal command line.
 *   `intent`   — cyan accent variant for the "live" / "primary CTA" feel.
 *   `default`  — outlined; muted text, secondary actions.
 *
 * Sizes: lg=44px height (touch target), md=36px, sm=32px. Bottom of `lg` hits
 * the iOS HIG 44pt minimum for tap targets.
 *
 * Focus ring uses `var(--ring)` (cyan-400) so keyboard nav is unmistakable.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'default',
			size = 'md',
			fullWidth = false,
			children,
			className,
			disabled,
			...rest
		},
		ref,
	) => {
		return (
			<button
				ref={ref}
				disabled={disabled}
				className={cn(
					// base
					'inline-flex items-center justify-center gap-1.5 rounded-md font-medium whitespace-nowrap',
					'transition-all duration-150 outline-none',
					'focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-background',
					'disabled:pointer-events-none disabled:opacity-40',
					'active:scale-[0.98]',
					// size — `lg` hits 44px (mobile HIG); h-9 = 36px desktop default
					size === 'sm' && 'h-8 px-3 text-sm',
					size === 'md' && 'h-9 px-4 text-sm',
					size === 'lg' && 'h-11 px-6 text-[15px]',
					// variant
					variant === 'primary' &&
						'bg-primary text-primary-foreground duration-200 shadow-[0_0_24px_-6px_rgba(34,211,238,0.35),var(--shadow-1)] hover:bg-primary hover:-translate-y-0.5 hover:shadow-[0_0_40px_-4px_rgba(34,211,238,0.6),var(--shadow-2)]',
					variant === 'intent' &&
						'bg-cyan-500/10 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/20 hover:border-cyan-400/60',
					variant === 'default' &&
						'bg-transparent text-foreground border border-border hover:bg-secondary hover:border-border-strong',
					// full width
					fullWidth && 'w-full',
					className,
				)}
				{...rest}
			>
				{children}
			</button>
		)
	},
)

Button.displayName = 'Button'

export type { ButtonSize, ButtonVariant }
export { Button }
